/* What is still being read. Every first read a page starts (useAsync with nothing yet to show) is counted here from the
   moment it starts until it answers, so the object's body can keep the height of the screen while data is on its way
   and give it back once the data has landed. A re-read of data already on screen is not counted: it moves nothing. */
import { useLayoutEffect, useState, useSyncExternalStore } from 'react'

let pending = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

/** Counts one first read; returns the function that ends it (a second call does nothing). */
export function hold(): () => void {
  let held = true
  pending++
  emit()
  return () => {
    if (!held) return
    held = false
    pending--
    emit()
  }
}

const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** How many first reads are on their way. */
export const pendingNow = (): number => pending

/** Whether any first read is still on its way. */
export const useLoading = (): boolean => useSyncExternalStore(subscribe, () => pending > 0, () => false)

/** Counts a read for as long as the calling component is mounted: the page's code on its way is the first read of all. */
export function useHolding(): void {
  useLayoutEffect(() => hold(), [])
}

/** Whether a view is still on its first read: true from its first render until every read that started with it has answered, and never again for that view. A read a component starts later, once the page is on screen, does not bring it back. */
export function useFirstRead(): boolean {
  const busy = useLoading()
  const [done, setDone] = useState(false)
  // Layout effects run children first, so on mount every read the page started is already counted.
  useLayoutEffect(() => {
    if (pending === 0) setDone(true)
  }, [busy])
  return !done
}
